import React, { useState, useEffect } from "react";
import api, { apiError } from "../lib/api";
import { Card, fmt, fmtDate } from "./ui-kit";
import { CheckCircle2, XCircle, AlertTriangle, Paperclip, Download, FileText } from "lucide-react";
import { toast } from "sonner";

function MissingInfo({ doc, onChange }) {
  const [items, setItems] = useState(doc.missing_info || []);
  if (!items.length) return null;
  const open = items.filter((i) => !i.resolved).length;
  const toggle = async (i) => {
    try { const { data } = await api.put(`/documents/${doc.id}/missing-info/${i.key}`, { resolved: !i.resolved }); setItems(data.missing_info); onChange && onChange(data); toast.success(!i.resolved ? `${i.label} marked received` : `${i.label} reopened`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className={open ? "border-[#91661E]/40" : ""} data-testid="missing-info">
      <h2 className="flex items-center gap-2 font-bold"><AlertTriangle className={`h-4 w-4 ${open ? "text-[#91661E]" : "text-primary"}`} /> Missing information <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-[#91661E]" data-testid="missing-open-count">{open} open</span></h2>
      <p className="mt-1 text-xs text-muted-foreground">Tracked questions left blank or answered "Need to obtain". Tick each one when the client provides it.</p>
      <ul className="mt-3 space-y-2 text-sm">{items.map((i) => (
        <li key={i.key} className="flex items-start gap-3 rounded-xl border border-border p-2">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={!!i.resolved} onChange={() => toggle(i)} data-testid={`missing-toggle-${i.key}`} />
          <div className="flex-1"><p className={i.resolved ? "line-through text-muted-foreground" : "font-medium"}>{i.label}</p><p className="text-xs text-muted-foreground">{i.section}{i.answer ? ` · answered "${i.answer}"` : " · not provided"}{i.resolved && ` · received, marked by ${i.resolved_by} ${fmt(i.resolved_at)}`}</p></div>
        </li>))}</ul>
    </Card>
  );
}

function Row({ label, value }) {
  return <div><dt className="text-xs text-muted-foreground">{label}</dt><dd className="font-medium">{value || "—"}</dd></div>;
}

function Attachment({ doc, a }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!a.type?.startsWith("image/")) return;
    let url;
    api.get(`/documents/${doc.id}/attachments/${a.key}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [doc.id, a.key, a.type]);
  const dl = async () => {
    try { const r = await api.get(`/documents/${doc.id}/attachments/${a.key}`, { params: { download: 1 }, responseType: "blob" }); const l = document.createElement("a"); l.href = URL.createObjectURL(r.data); l.download = a.name; l.click(); URL.revokeObjectURL(l.href); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <li className="flex items-center gap-3 rounded-xl border border-border p-2" data-testid={`attachment-${a.key}`}>
      {a.type?.startsWith("image/") ? (src ? <img src={src} alt={a.label} className="h-16 w-20 cursor-pointer rounded object-cover" onClick={() => window.open(src, "_blank")} /> : <div className="h-16 w-20 animate-pulse rounded bg-muted" />) : <FileText className="h-12 w-12 text-primary" />}
      <div className="min-w-0 flex-1"><p className="text-sm font-medium">{a.label}</p><p className="truncate text-xs text-muted-foreground">{a.name} · {Math.round((a.size || 0) / 1024)} KB</p></div>
      <button onClick={dl} className="rounded-lg p-2 hover:bg-muted" aria-label="Download" data-testid={`attachment-download-${a.key}`}><Download className="h-4 w-4" /></button>
    </li>
  );
}

export function SubmissionView({ doc }) {
  const s = doc.submission || {};
  const ts = doc.timestamps || {};
  const isConsent = doc.kind === "consent";
  return (
    <div className="space-y-4" data-testid="submission-view">
      {!isConsent && <MissingInfo doc={doc} />}
      {!isConsent && (s.attachments || []).length > 0 && (
        <Card data-testid="submission-attachments">
          <h2 className="flex items-center gap-2 font-bold"><Paperclip className="h-4 w-4 text-primary" /> Attached documents</h2>
          <ul className="mt-3 grid gap-3 sm:grid-cols-2">{s.attachments.map((a) => <Attachment key={a.key} doc={doc} a={a} />)}</ul>
        </Card>)}
      <Card>
        <h2 className="font-bold">{isConsent ? "Client details" : "Submitted answers"}</h2>
        {isConsent ? (
          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3" data-testid="consent-details">
            <Row label="Client" value={`${s.first_name || ""} ${s.last_name || ""}`} /><Row label="Date of birth" value={s.dob} /><Row label="Age" value={s.age != null ? `${s.age}${s.is_minor ? " (minor)" : ""}` : null} />
            <Row label="Phone" value={s.phone} /><Row label="Email" value={s.email} /><Row label="Service location" value={s.service_location} />
            {s.is_minor && <><Row label="Parent / guardian" value={s.guardian_name} /><Row label="Relationship" value={s.guardian_relationship} /><Row label="Guardian phone" value={s.guardian_phone} /></>}
            <Row label="Language" value={s.language === "es" ? "Español" : "English"} />
          </dl>
        ) : (
          <div className="mt-3 space-y-4 text-sm" data-testid="intake-answers">
            {(s.sections || []).map((sec) => (
              <div key={sec.title}><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{sec.title}</p>
                <dl className="mt-1 grid gap-2 sm:grid-cols-2">{sec.fields.map((f) => <Row key={f.key} label={f.label} value={Array.isArray(s.answers?.[f.key]) ? s.answers[f.key].join(", ") : String(s.answers?.[f.key] ?? "")} />)}</dl></div>))}
            {s.assisted_by && <p className="text-xs text-muted-foreground">Assisted by: {s.assisted_by}</p>}
          </div>
        )}
      </Card>
      {isConsent && (
        <Card>
          <h2 className="font-bold">Acknowledgments</h2>
          <ul className="mt-3 space-y-2 text-sm" data-testid="consent-acks">{(s.acknowledgments || []).map((a, i) => (
            <li key={i} className="flex items-start gap-2">{a.accepted ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#A4473C]" />}<span>{a.label}</span></li>))}</ul>
          {(s.consent_blocks || []).length > 0 && <details className="mt-4 text-sm"><summary className="cursor-pointer text-xs font-semibold text-primary">Show the consent text the client reviewed</summary>
            <div className="mt-2 space-y-3">{s.consent_blocks.map((b, i) => <div key={i}><p className="font-semibold">{b.title}</p><p className="whitespace-pre-wrap text-muted-foreground">{b.body}</p></div>)}</div></details>}
        </Card>)}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <h2 className="font-bold">Signature</h2>
          {s.signature ? <img src={s.signature} alt="Client signature" className="mt-3 max-h-32 rounded-xl border border-border bg-white p-2" data-testid="submission-signature" /> : <p className="mt-2 text-sm text-muted-foreground">No signature image.</p>}
          <p className="mt-2 text-xs text-muted-foreground">{s.signer_name || doc.client_name} · signed {fmt(ts.signature_at || doc.created_at)} · electronic signature agreed</p>
        </Card>
        <Card>
          <h2 className="font-bold">{isConsent ? "Live client photo" : "Pre-Intake photo"}</h2>
          {s.client_photo || s.photo ? <img src={s.client_photo || s.photo} alt="Client" className="mt-3 h-48 rounded-xl object-cover" data-testid="submission-photo" /> : <p className="mt-2 text-sm text-muted-foreground">No photo captured.</p>}
          {ts.photo_at && <p className="mt-2 text-xs text-muted-foreground">Captured {fmt(ts.photo_at)}</p>}
        </Card>
      </div>
      <p className="text-xs text-muted-foreground">Submitted {fmt(doc.created_at)} · Relevant date {fmtDate(doc.relevant_date)} · Record {doc.ref} · Stored permanently on Airfistuus.</p>
    </div>
  );
}
